import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';

import { TtqAngleRelationIcon } from './TtqAngleRelationIcon';
import type { TtqBehaviorId } from './ttqBehaviorCatalog';
import { TtqWorkbenchSourceParticle } from './TtqWorkbenchSourceParticle';
import { writeTtqSessionUiState } from './ttqSessionUiState';
import { useParticleEmbeddedImage } from './useParticleEmbeddedImage';
import { fetchWorkbenchParticle, getWorkbenchParticleCache } from './ttqWorkbenchParticleCache';
import { ttqService, type TtqSession } from '../../services/ttqService';
import type { KnowledgeParticle } from '../../types/domain';

function readQuestionText(questions: unknown): string {
  if (typeof questions === 'string') return questions;
  if (questions && typeof questions === 'object' && 'main' in questions) {
    return String((questions as { main?: string }).main || '');
  }
  return '';
}

export function useTtqWorkbenchContext(ttqaId: string, particleId: string) {
  const cached = particleId ? getWorkbenchParticleCache(particleId) : null;
  const [session, setSession] = useState<TtqSession | null>(null);
  const [particle, setParticle] = useState<KnowledgeParticle | null>(cached);
  const [loading, setLoading] = useState(!cached);
  const [error, setError] = useState<string | null>(null);
  const { data: sourceImage, loading: imageLoading } = useParticleEmbeddedImage(
    particleId,
    Boolean(particleId),
  );

  useEffect(() => {
    let active = true;
    const hit = particleId ? getWorkbenchParticleCache(particleId) : null;
    if (hit) {
      setParticle(hit);
      setLoading(false);
    } else {
      setLoading(true);
    }
    void Promise.all([
      ttqService.getSession(ttqaId),
      particleId ? fetchWorkbenchParticle(particleId) : Promise.resolve(null),
    ])
      .then(([s, p]) => {
        if (!active) return;
        setSession(s);
        if (p) setParticle(p);
      })
      .catch((e) => active && setError(e instanceof Error ? e.message : '加载失败'))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [ttqaId, particleId]);

  const questionText = useMemo(
    () => readQuestionText(particle?.questions),
    [particle?.questions],
  );
  const answerText = useMemo(
    () => readQuestionText(particle?.answers),
    [particle?.answers],
  );

  return { session, particle, loading, error, sourceImage, imageLoading, questionText, answerText };
}

type TtqWorkbenchShellProps = {
  ttqaId: string;
  title: string;
  angleId: TtqBehaviorId;
  loading?: boolean;
  error?: string | null;
  restoreParticleIds?: string[];
  toolbar?: React.ReactNode;
  children: React.ReactNode;
};

export function TtqWorkbenchShell({
  ttqaId,
  title,
  angleId,
  loading,
  error,
  restoreParticleIds = [],
  toolbar,
  children,
}: TtqWorkbenchShellProps) {
  return (
    <div className="mx-auto max-w-6xl">
      <header className="sticky top-0 z-30 border-b border-border bg-canvas/95 px-4 py-3 backdrop-blur supports-[backdrop-filter]:bg-canvas/80">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <div className="flex min-w-0 items-center gap-2">
            <Link
              to={`/ttq/${ttqaId}`}
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-primary hover:bg-primary/10"
              aria-label="返回品题会话"
              title="返回品题会话"
              onClick={() => {
                writeTtqSessionUiState(ttqaId, {
                  selectedParticleIds: restoreParticleIds,
                  bottomMenuOpen: true,
                  restoreMenu: true,
                });
              }}
            >
              <ArrowLeft size={18} />
            </Link>
            <TtqAngleRelationIcon
              angleId={angleId}
              size="sm"
              captionOverride={title}
              className="shrink-0"
            />
            <h1 className="sr-only">{title}</h1>
          </div>
          {toolbar ? (
            <div className="ml-auto flex min-w-0 flex-1 basis-full justify-end sm:basis-auto">
              {toolbar}
            </div>
          ) : null}
        </div>
      </header>

      <div className="space-y-4 px-4 py-4">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="animate-spin text-primary" /></div>
        ) : null}
        {error ? <p className="text-sm text-danger">{error}</p> : null}
        {!loading && !error ? children : null}
      </div>
    </div>
  );
}

/** @deprecated 使用 TtqWorkbenchSourceParticle */
export const TtqSourceParticlePanel = TtqWorkbenchSourceParticle;

export { TtqWorkbenchSourceParticle };
export type { TtqWorkbenchSourceParticleProps } from './TtqWorkbenchSourceParticle';
